import { Pencil, Search, Trash2, X } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Card } from "../../components/ui/card";
import { ContentLayout } from "../../components/admin-panel/content-layout";
import ModalPopUp from "../../components/ModalPopUp";
import DeleteModal from "../../components/deleteModal";
import Pagination from "../../components/Pagination";
import Upload from "../../assets/images/upload.svg";
import { FaFilePdf } from "react-icons/fa6";
import { FaFileExcel } from "react-icons/fa6";
import { BiSolidFileJpg } from "react-icons/bi";
import { IoDocumentText } from "react-icons/io5";
import { BiSolidFilePng } from "react-icons/bi";
import UserService from "../../services/user/user";
import { toastError, toastSuccess } from "../../components/ToastNotification";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import Loader from "../../assets/images/loader.gif";
import { FaVideo } from "react-icons/fa";
import ChatService from "../../services/chat/chat";
import { useNavigate } from "react-router";
import { Progress } from "../../components/ui/progress";
import { Input } from "../../components/ui/input";
import SubscriptionService from "../../services/subscription/subscription";
import IndividualSubscriptionService from "../../services/subscription/individualSubscription";
import Organizationpopup from "../../components/popup/Organizationpopup";
import IndividualPopup from "../../components/popup/IndividualPopup";

const SEARCH_DEBOUNCE_MS = 400;

/** In-memory cache so returning to this page can show the last list while refetching. */
const userTableCache = new Map();

function getUserTableCache(adminId) {
  if (!adminId) return null;
  return userTableCache.get(String(adminId)) ?? null;
}

function setUserTableCache(adminId, snapshot) {
  if (!adminId) return;
  userTableCache.set(String(adminId), snapshot);
}

const User = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state?.superAdmin?.user);

  const store = useSelector((state) => state);
  console.log("🚀  store:", user)

  const [adminData, setAdminData] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null); // Store the selected file
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [fileDeleteLoading, setFileDeleteLoading] = useState("");
  const [progress, setprogress] = React.useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0); // This can be dynamic based on your data
  /** Full-page loader only for the first user list fetch (not search/pagination). */
  const [isPageLoading, setIsPageLoading] = useState(false);
  /** Loader inside the users table while searching or changing page. */
  const [isTableLoading, setIsTableLoading] = useState(false);
  const hasLoadedUsersOnceRef = useRef(false);
  const [files, setFiles] = useState("");
  const [isUploadSubscriptionPopupOpen, setIsUploadSubscriptionPopupOpen] =
    useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [hasActiveSubscription, setHasActiveSubscription] = useState(true);
  const [isSubscriptionStatusLoading, setIsSubscriptionStatusLoading] =
    useState(true);
  const skipDebouncedSearchEffect = useRef(true);

  const isAdminOrOrganizationAdmin =
    user?.admintype === "Admin" || user?.admintype === "OrganizationAdmin";
  const isIndividualAdmin = user?.admintype === "IndividualAdmin";
  const shouldBlockUploadForSubscription =
    !isSubscriptionStatusLoading &&
    !hasActiveSubscription &&
    (isAdminOrOrganizationAdmin || isIndividualAdmin);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const getAllUser = async (showLoader = true, overrides = {}) => {
    if (!user?._id) return;
    const page = overrides.page ?? currentPage;
    const search =
      overrides.search !== undefined ? overrides.search : debouncedSearch;

    const usePageLoader =
      showLoader && !hasLoadedUsersOnceRef.current;

    if (showLoader) {
      if (usePageLoader) setIsPageLoading(true);
      else setIsTableLoading(true);
    }
    try {
      const response = await UserService?.getAllUser(
        user?._id,
        page,
        limit,
        search
      );
      if (response) {
        const rows = response?.data?.data ? response?.data?.data : [];
        const nextTotal = Math.ceil(response.data?.totalUser / limit);
        setAdminData(rows);
        setTotalPages(nextTotal);
        hasLoadedUsersOnceRef.current = true;
        setUserTableCache(user._id, {
          rows,
          totalPages: nextTotal,
          page,
          search,
          searchInput,
        });
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    } finally {
      if (showLoader) {
        if (usePageLoader) setIsPageLoading(false);
        else setIsTableLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!user?._id) {
      skipDebouncedSearchEffect.current = true;
      return;
    }
    if (skipDebouncedSearchEffect.current) {
      skipDebouncedSearchEffect.current = false;
      return;
    }
    setCurrentPage(1);
    getAllUser(true, { page: 1, search: debouncedSearch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, user?._id]);

  // Function to handle page change
  const handlePageChange = (page, event) => {
    event.preventDefault();
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      getAllUser(true, { page });
    }
  };

  const deleteUser = async (userId) => {
    try {
      const response = await UserService?.deleteUser(userId);
      if (response) {
        toastSuccess(response?.data?.message);
        getAllUser();
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    }
  };

  const getCompany = async () => {
    try {
      const response = await UserService?.getCompany();
      if (response) {
        setCompanyName(response.data.data?.name);
        setCompanyId(response.data.data?._id);
        setFiles(response.data.data?.document);
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    }
  };

  const getSubscriptionStatus = async () => {
    if (!user?.admintype) {
      setHasActiveSubscription(true);
      setIsSubscriptionStatusLoading(false);
      return;
    }

    const isAdminUser =
      user?.admintype === "Admin" ||
      user?.admintype === "OrganizationAdmin" ||
      user?.admintype === "IndividualAdmin";

    if (!isAdminUser) {
      setHasActiveSubscription(true);
      setIsSubscriptionStatusLoading(false);
      return;
    }

    setIsSubscriptionStatusLoading(true);
    try {
      let response;
      if (user?.admintype === "IndividualAdmin") {
        response = await IndividualSubscriptionService?.activeSubscription();
      } else {
        response = await SubscriptionService?.getSubscription();
      }

      setHasActiveSubscription(
        Boolean(response?.data?.data?.active_subscription)
      );
    } catch (error) {
      if (error?.response?.status === 404) {
        setHasActiveSubscription(false);
      } else {
        setHasActiveSubscription(true);
      }
    } finally {
      setIsSubscriptionStatusLoading(false);
    }
  };

  useLayoutEffect(() => {
    if (!user?._id) {
      setIsPageLoading(false);
      setIsTableLoading(false);
      hasLoadedUsersOnceRef.current = false;
      setAdminData([]);
      userTableCache.clear();
      return;
    }

    const cached = getUserTableCache(user._id);
    if (cached) {
      setAdminData(cached.rows);
      setTotalPages(cached.totalPages);
      setCurrentPage(cached.page);
      setDebouncedSearch(cached.search);
      setSearchInput(cached.searchInput);
      setIsPageLoading(false);
      hasLoadedUsersOnceRef.current = true;
    } else {
      hasLoadedUsersOnceRef.current = false;
      setIsPageLoading(true);
    }
  }, [user?._id]);

  useEffect(() => {
    if (!user?._id) {
      setHasActiveSubscription(true);
      setIsSubscriptionStatusLoading(false);
      return;
    }

    getCompany();
    getSubscriptionStatus();

    const cached = getUserTableCache(user._id);
    if (cached) {
      getAllUser(false, { page: cached.page, search: cached.search });
    } else {
      getAllUser(true, { page: 1, search: debouncedSearch });
    }
    // Refetch when admin is available; use cache for instant UI (layout effect) + background refresh when cached.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleDeleteFile = async (id) => {
    setFileDeleteLoading(id);
    try {
      const response = await ChatService?.deleteFile(companyId, id);
      if (response) {
        setFileDeleteLoading("");
        getCompany();
        toastSuccess(response?.data?.message || "Deleted successfully");
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    }
    // setFiles(null);
  };

  /* const handleClick = () => {
    navigate("/documents");
  }; */

  /*  const handleSubmit = async (values) => {
    setIsFileLoading(true);
    const fileArray = Array.from(values.file);

    try {
      if (companyId && companyName && fileArray.length > 0) {
        const formData = new FormData();
        fileArray.forEach((file) => formData.append("file", file));
        formData.append("companyId", companyId);
        formData.append("category", companyName);
        const response = await UserService?.adminFileUpload(formData);
        if (response) {
          toastSuccess(`File uploaded successfully!`);
          getCompany();
        }
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    } finally {
      setIsFileLoading(false);
    }
  }; */

  const MAX_CHUNK_SIZE = 4 * 1024 * 1024; // 4MB in bytes
  const VIDEO_TYPE = "video";

  // const handleSubmit = async (values) => {
  //   setIsFileLoading(true);
  //   const fileArray = Array.from(values.file); // Get all files from Formik

  //   let totalChunks = 0;
  //   let completedChunks = 0;

  //   // Calculate total chunks for progress tracking
  //   fileArray.forEach((file) => {
  //     if (file.type.startsWith(VIDEO_TYPE) && file.size > MAX_CHUNK_SIZE) {
  //       totalChunks += Math.ceil(file.size / MAX_CHUNK_SIZE);
  //     } else {
  //       totalChunks += 1;
  //     }
  //   });

  //   try {
  //     if (companyId && companyName && fileArray.length > 0) {
  //       for (const file of fileArray) {
  //         if (file.type.startsWith(VIDEO_TYPE) && file.size > MAX_CHUNK_SIZE) {
  //           // Upload video file in chunks
  //           await uploadFileInChunks(
  //             file,
  //             companyId,
  //             companyName,
  //             totalChunks,
  //             completedChunks
  //           );
  //         } else {
  //           // Small files can be uploaded directly
  //           const formData = new FormData();
  //           formData.append("file", file);
  //           formData.append("companyId", companyId);
  //           formData.append("category", companyName);
  //           await uploadFile(formData, completedChunks, totalChunks);
  //           completedChunks++; // Update completed chunks after each small file
  //         }
  //       }

  //       toastSuccess(`File uploaded successfully!`);
  //       getCompany();
  //     }
  //   } catch (error) {
  //     toastError(error?.response?.data?.message);
  //   } finally {
  //     setIsFileLoading(false);
  //   }
  // };

  const handleSubmit = async (values) => {
    setIsFileLoading(true);
    const fileArray = Array.from(values.file);
    setUploadedFiles(fileArray);

    try {
      console.log(
        "🚀 ~ handleSubmit ~ values:",
        companyId,
        companyName,
        fileArray.length > 0
      );
      if (fileArray.length > 0) {
        const formData = new FormData();
        // Loop through each file and upload them one by one
        fileArray.forEach(
          (file) => formData.append("file", file) // Append each file individually
        );
        {
          user.admintype === "OrganizationAdmin" &&
          formData.append("companyId", companyId);
        }
        formData.append("tenantId", user?.tenant_id)
        formData.append("category", user?.category);
        console.log("DFasds gasdgasdas");

        const response = await UserService?.adminFileUpload(formData, {
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setprogress(percentCompleted); // Update upload progress
          },
        });
        if (response) {
          toastSuccess(`File uploaded successfully!`);
          getCompany();
        }
      }
      setprogress(0);
    } catch (error) {
      toastError(error?.response?.data?.message);
    } finally {
      setIsFileLoading(false);
      setUploadedFiles([]);
      setprogress(0);
    }
  };

  // Function to upload large files in chunks
  const uploadFileInChunks = async (
    file,
    companyId,
    category,
    totalChunks,
    completedChunks
  ) => {
    console.log("-----------ita large video--------------");
    const chunkCount = Math.ceil(file.size / MAX_CHUNK_SIZE);

    for (let i = 0; i < chunkCount; i++) {
      const start = i * MAX_CHUNK_SIZE;
      const end = Math.min(start + MAX_CHUNK_SIZE, file.size);
      const chunkBlob = file.slice(start, end);

      const chunkFile = new File([chunkBlob], file.name, {
        type: file.type,
        lastModified: file.lastModified,
      });
      console.log("🚀  User  chunkFile:", chunkFile);

      const chunkFormData = new FormData();
      chunkFormData.append("file", chunkFile);
      chunkFormData.append("companyId", companyId);
      chunkFormData.append("category", category);

      try {
        await uploadFile(chunkFormData, completedChunks + i, totalChunks);
      } catch (error) {
        console.error(`Error uploading chunk ${i + 1}:`, error);
        break;
      }
    }
    completedChunks += chunkCount; // Update completed chunks after each file is fully uploaded
  };

  // Upload helper function with cumulative progress tracking
  const uploadFile = async (formData, completedChunks, totalChunks) => {
    try {
      await UserService?.adminFileUpload(formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            ((completedChunks + progressEvent.loaded / progressEvent.total) *
              100) /
            totalChunks
          );
          // setprogress(percentCompleted); // Update single progress bar for all chunks
        },
      });
    } catch (error) {
      throw error; // Rethrow to handle in the main function
    }
  };

  // Validation schema using Yup
  const validationSchema = Yup.object({
    file: Yup.mixed().required("A file is required"),
  });

  // Using Formik for file input handling
  const formik = useFormik({
    initialValues: {
      file: null,
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  // Function to handle file input change and trigger form submission
  // const handleFileChange = (event) => {
  //   const files = event.currentTarget.files;
  //   if (files.length > 0) {
  //     formik.setFieldValue("file", files);
  //     setTimeout(() => {
  //       formik.submitForm();
  //     }, 300);
  //   }
  // };

  const handleFileChange = (event) => {
    const files = event.currentTarget.files;

    if (!files || files.length === 0) {
      return;
    }

    if (shouldBlockUploadForSubscription) {
      event.target.value = "";
      setIsUploadSubscriptionPopupOpen(true);
      return;
    }

    formik.setFieldValue("file", files);

    setTimeout(() => {
      formik.submitForm();
    }, 300);
  };

  /*  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    let compressedFiles = []; 

    for (const file of files) {
      const fileType = file.type;

      if (fileType.startsWith("video/")) {
        try {
          setIsFileLoading(true);
          const compressedVideo = await compressVideo(file, 0.2); 
          compressedFiles.push(compressedVideo);
        } catch (error) {
          console.error("Error compressing video:", error);
          compressedFiles.push(file); 
        } 
      } else {
        setIsFileLoading(true);
        compressedFiles.push(file);
      }
    }

    formik.setFieldValue("file", compressedFiles);

    // Delay submitting the form briefly to ensure files are set
    setTimeout(() => {
      formik.submitForm();
    }, 300);
  }; */

  const compressVideo = async (file, scaleFactor) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.src = URL.createObjectURL(file);
      video.muted = true;

      // Determine the mimeType and extension based on the file type
      const supportedTypes = {
        "video/mp4": { mimeType: "video/mp4; codecs=vp9", extension: "mp4" },
        "video/quicktime": { mimeType: "video/quicktime", extension: "mov" },
        "video/x-ms-wmv": { mimeType: "video/x-ms-wmv", extension: "wmv" },
        "video/x-msvideo": { mimeType: "video/x-msvideo", extension: "avi" },
        "video/x-flv": { mimeType: "video/x-flv", extension: "flv" },
        "video/x-matroska": { mimeType: "video/x-matroska", extension: "mkv" },
      };

      // Check if the file type is supported; default to 'mp4' if not
      const fileType = supportedTypes[file.type] ? file.type : "video/mp4";
      const { mimeType, extension } =
        supportedTypes[fileType] || supportedTypes["video/mp4"];

      video.onloadeddata = async () => {
        if (!video.videoWidth || !video.videoHeight) {
          return reject("Video dimensions are not available.");
        }

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Set canvas size to scale down the resolution
        canvas.width = Math.floor(video.videoWidth * scaleFactor);
        canvas.height = Math.floor(video.videoHeight * scaleFactor);

        const recordedChunks = [];
        const mediaRecorder = new MediaRecorder(canvas.captureStream(15), {
          mimeType: mimeType,
          videoBitsPerSecond: 500000, // Set a lower bitrate (e.g., 500 kbps)
        });

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            recordedChunks.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          const blob = new Blob(recordedChunks, { type: fileType });
          const originalName = file.name.split(".").slice(0, -1).join(".");
          const compressedFile = new File(
            [blob],
            `${originalName}.${extension}`,
            {
              type: fileType,
            }
          );

          if (compressedFile.size > file.size * 0.2) {
            reject("Compressed file is not below the 80% reduction goal.");
          } else {
            resolve(compressedFile);
          }
        };

        video.play();
        mediaRecorder.start();

        video.ontimeupdate = () => {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        };

        video.onended = () => {
          mediaRecorder.stop();
        };

        video.onerror = () => reject("Error processing video.");
      };

      video.onerror = () => reject("Error loading video.");
    });
  };

  // Helper function to determine file type for preview icons
  const renderFilePreview = (file) => {
    if (!file) return null;

    const fileName = file?.substring(file.lastIndexOf("/") + 1).toLowerCase();
    if (fileName.endsWith("pdf")) {
      return <FaFilePdf className="text-[#CC0000] text-2xl" />;
    } else if (fileName.endsWith("doc") || fileName.endsWith("docx")) {
      return <IoDocumentText className="text-[#3980FB] text-2xl" />;
    } else if (fileName.endsWith("xlsx")) {
      return <FaFileExcel className="text-[#00733B] text-2xl" />;
    } else if (fileName.endsWith("jpg")) {
      return <BiSolidFileJpg className="text-[#2C7FC3] text-2xl" />;
    } else if (fileName.endsWith("png")) {
      return <BiSolidFilePng className="text-[#F05C58] text-2xl" />;
    } else {
      return <IoDocumentText className="text-2xl" />; // Default icon for other file types
    }
  };

  const renderFileIcon = (fileType) => {
    switch (fileType) {
      case "pdf":
        return <FaFilePdf className="text-[#CC0000] text-2xl" />;
      case "doc":
      case "docx":
        return <IoDocumentText className="text-[#3980FB] text-2xl" />;
      case "xlsx":
      case "xls":
        return <FaFileExcel className="text-[#00733B] text-2xl" />;
      case "jpg":
      case "jpeg":
        return <BiSolidFileJpg className="text-[#2C7FC3] text-2xl" />;
      case "png":
        return <BiSolidFilePng className="text-[#F05C58] text-2xl" />;
      case "mp4":
      case "mov":
      case "wmv":
      case "avi":
      case "flv":
      case "mkv":
        return <FaVideo className="text-primary text-2xl" />; // Video icon
      default:
        return <IoDocumentText className="text-[#9E9E9E] text-2xl" />;
    }
  };

  return (
    <ContentLayout>
      {isPageLoading ? (
        <div className="flex justify-center items-center h-[calc(100vh-180px)]">
          <img src={Loader} alt="Loader" className="w-20" />
        </div>
      ) : (
        <>
          <Card className="grid gap-5 mb-5">
            <div className="flex flex-col justify-center gap-4">
              <div className="flex justify-between items-center">
                {
                  user?.admintype === "Admin" || user?.admintype === "OrganizationAdmin" ? (
                    <h1 className="font-semibold text-xl">
                      Company name : {companyName}
                    </h1>
                  ) : (
                    <h1 className="font-semibold text-xl">
                      User name : {user?.name}
                    </h1>
                  )
                }
                {/* <button
                  onClick={handleClick}
                  className="bg-primary hover:bg-btnHover text-white text-base py-2 px-3 rounded-md"
                >
                  View Document
                </button> */}
              </div>
              <h4 className="font-semibold text-xl">Upload Document</h4>
            </div>
            <div className="max-w-2xl mx-auto w-full">
              {/* {!isFileLoading ? (
            <div>
              <img src={Loader} alt="Loader" className="w-20" />
            </div>
          ) : ( */}

              <form onSubmit={formik.handleSubmit}>
                <label
                  htmlFor="uploads"
                  className="border-dashed border-2 rounded-lg !border-primary/50 w-full p-5 lg:p-10 m-auto cursor-pointer grid gap-2 justify-center"
                >
                  <>
                    <div className="flex justify-center mb-2">
                      <img src={Upload} alt="Upload File" />
                    </div>
                    <h6 className="text-white/60 text-xl text-center">
                      Click to upload your file
                    </h6>
                    <input
                      type="file"
                      id="uploads"
                      name="file"
                      hidden
                      onChange={handleFileChange}
                      accept=".txt,.pdf,.docx,.rtf,.doc,.xls,.xlsx,.mp4,.mov,.wmv,.avi,.flv,.mkv"
                      multiple
                    />
                  </>
                </label>
                {shouldBlockUploadForSubscription && (
                  <div className="mt-4 flex justify-center">
                    {isAdminOrOrganizationAdmin && (
                      <Organizationpopup
                        triggerLabel="Subscribe to upload"
                        open={isUploadSubscriptionPopupOpen}
                        onOpenChange={setIsUploadSubscriptionPopupOpen}
                        hideTrigger
                      />
                    )}
                    {isIndividualAdmin && (
                      <IndividualPopup
                        triggerLabel="Subscribe to upload"
                        open={isUploadSubscriptionPopupOpen}
                        onOpenChange={setIsUploadSubscriptionPopupOpen}
                        hideTrigger
                      />
                    )}
                  </div>
                )}
                {formik.errors.file && formik.touched.file && (
                  <div className="text-red-500 text-sm mt-2">
                    {formik.errors.file}
                  </div>
                )}
              </form>
              {/* )} */}
            </div>


          </Card>

          {uploadedFiles &&
            uploadedFiles?.length > 0 &&
            uploadedFiles?.map((res) => {
              const fileExtension = res?.name?.split(".").pop().toLowerCase();
              return (
                <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fit,_minmax(500px,_1fr))] gap-5 my-4">
                  <div
                    className={`p-4 bg-sidebar rounded-lg grid gap-4 border ${fileExtension === "pdf"
                      ? "border-[#CC0000]"
                      : fileExtension === "doc" || fileExtension === "docx"
                        ? "border-[#3980FB]"
                        : fileExtension === "xlsx" || fileExtension === "xls"
                          ? "border-[#00733B]"
                          : fileExtension === "jpg" || fileExtension === "jpeg"
                            ? "border-[#2C7FC3]"
                            : fileExtension === "mp4" ||
                              fileExtension === "avi" ||
                              fileExtension === "mkv" ||
                              fileExtension === "mov" ||
                              fileExtension === "wmv" ||
                              fileExtension === "flv"
                              ? "border-primary/50"
                              : "border-primary/50"
                      }`}
                  >
                    <div className="flex items-center gap-4">
                      <div>{renderFileIcon(fileExtension)}</div>
                      <div className="flex flex-col justify-between w-full gap-3">
                        <div className="flex items-start justify-between gap-3 w-full h-fit">
                          <h6 className="text-white/80 text-xl leading-none">
                            {res?.name}
                          </h6>
                          <h6 className="text-sm text-white/70 leading-none">
                            {progress === 100
                              ? "Uploading. . ."
                              : `${progress}%`}
                          </h6>
                        </div>
                      </div>
                    </div>
                    <div className="w-full">
                      <Progress value={progress} className="w-[50%]" />
                    </div>
                  </div>
                </div>
              );
            })}

          <Card className="grid gap-5 mt-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4"  >
                <h1 className="font-semibold text-xl">User</h1>

                <div className="relative w-full sm:max-w-xs shrink-0">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40 pointer-events-none" />
                  <Input
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search users…"
                    className="pl-9 bg-darkGray border-border text-white placeholder:text-white/40"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 w-full sm:w-auto">
                {!isSubscriptionStatusLoading && !hasActiveSubscription ? (
                  <>
                    {(user?.admintype === "Admin" ||
                      user?.admintype === "OrganizationAdmin") && (
                      <Organizationpopup triggerLabel="+ Add New" />
                    )}
                    {user?.admintype === "IndividualAdmin" && (
                      <IndividualPopup triggerLabel="+ Add New" />
                    )}
                  </>
                ) : (
                  <ModalPopUp type="add" getAllUser={getAllUser} data="" />
                )}
              </div>
            </div>
            <div className="!border-border !border rounded-lg relative w-full overflow-auto min-h-[200px]">
              <Table className="rounded-lg overflow-hidden">
                <TableHeader>
                  <TableRow className="!bg-darkGray text-white">
                    <TableHead className="w-[100px] text-white">No.</TableHead>
                    <TableHead className="text-white">Name</TableHead>
                    <TableHead className="text-white">Number</TableHead>
                    <TableHead className="text-white">Email</TableHead>
                    <TableHead className="text-white">Type</TableHead>
                    {/* <TableHead className="text-white">Company</TableHead> */}
                    {/* <TableHead className="text-white">Password</TableHead> */}
                    <TableHead className="w-14 text-center text-white">
                      Action
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isTableLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center h-40">
                        <div className="flex justify-center items-center">
                          <img src={Loader} alt="Loader" className="w-20" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : adminData.length > 0 ? (
                    adminData.map((adminData, index) => (
                      <TableRow key={adminData._id ?? adminData.adminData}>
                        <TableCell>
                          {(currentPage - 1) * limit + index + 1}
                        </TableCell>
                        <TableCell>{adminData.name}</TableCell>
                        <TableCell>{adminData.mobileNumber}</TableCell>
                        <TableCell>{adminData.email}</TableCell>
                        <TableCell>{adminData.userType}</TableCell>
                        {/* <TableCell>{adminData?.company?.name}</TableCell> */}
                        {/* <TableCell>{"*".repeat(10)}</TableCell> */}
                        <TableCell className="w-14">
                          <div className="flex items-center justify-center gap-2">
                            {/* <div
                              className="group border rounded-md p-1 border-[#7064E9] hover:bg-[#7064E9] duration-200 cursor-pointer"
                              onClick={() => handleOpen(adminData)}
                            >
                              <Pencil className="w-4 h-4 text-[#7064E9] group-hover:text-white" />
                            </div> */}
                            <ModalPopUp
                              type="edit"
                              getAllUser={getAllUser}
                              data={adminData}
                            />
                            <DeleteModal
                              handleDelete={() => deleteUser(adminData?._id)}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center h-20 font-bold"
                      >
                        User Not Found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            {adminData.length > 0 && !isTableLoading && (
              <div className="flex justify-end items-center">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  handlePageChange={handlePageChange}
                />
              </div>
            )}
          </Card>
        </>
      )}
    </ContentLayout>
  );
};

export default User;
